import ImageCard from '@components/common/ImageCard';
import Icon from '@components/common/Icon';
import { getDataUrl } from '@lib/utils';

const PrisonChessImpactSection = () => {
  const stats = [
    { value: '50+', label: 'Inmates Coached' },
    { value: '100+', label: 'Hours Taught' },
    { value: '2', label: 'Facilities Visited' }
  ];

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-theme-1)' }}>
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'var(--color-accent-1)', borderColor: 'var(--color-accent-3)', borderWidth: '1px', borderStyle: 'solid' }}>
            <Icon name="social-impact" size="md" style={{ color: 'var(--color-accent-6)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--color-accent-7)' }}>
              Social Impact Through Chess
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Prison Chess Initiative
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Using chess as a tool for rehabilitation and personal growth, teaching strategy,
            patience, and critical thinking to inmates at San Fernando City Jail and other facilities.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Image 1 */}
          <div
            className="rounded-xl overflow-hidden border shadow-card hover:shadow-card-hover transition-all duration-300"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-card-border)'
            }}
          >
            <ImageCard
              src={getDataUrl('chess/recognitions/inmate_chess_1.webp')}
              alt="Teaching chess to inmates"
              aspectRatio="aspect-[4/3]"
              rounded={false}
              shadow={false}
            />
            <div className="p-6">
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                Teaching Sessions
              </h3>
              <p
                className="leading-relaxed"
                style={{ color: 'var(--color-card-text)' }}
              >
                Conducting weekly chess lessons focusing on strategic thinking, patience,
                and problem-solving skills that extend beyond the board.
              </p>
            </div>
          </div>

          {/* Image 2 */}
          <div
            className="rounded-xl overflow-hidden border shadow-card hover:shadow-card-hover transition-all duration-300"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-card-border)'
            }}
          >
            <ImageCard
              src={getDataUrl('chess/recognitions/inmate_chess_2.webp')}
              alt="Group chess training with inmates"
              aspectRatio="aspect-[4/3]"
              rounded={false}
              shadow={false}
            />
            <div className="p-6">
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                Building Community
              </h3>
              <p
                className="leading-relaxed"
                style={{ color: 'var(--color-card-text)' }}
              >
                Creating a supportive environment where inmates can develop new skills,
                build confidence, and engage in positive mental activities.
              </p>
            </div>
          </div>
        </div>

        {/* Impact Stats */}
        <div
          className="rounded-xl border p-6 sm:p-8 mb-12"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-card-border)'
          }}
        >
          <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
                  style={{ color: 'var(--color-accent-6)' }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-xs sm:text-sm font-medium"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recognition Card */}
        <div
          className="rounded-xl border overflow-hidden shadow-card"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-accent-6)',
            borderWidth: '2px'
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 p-6 sm:p-8 items-center">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--color-accent-1)' }}>
                <span className="text-xs font-bold" style={{ color: 'var(--color-accent-7)' }}>
                  OFFICIAL RECOGNITION
                </span>
              </div>
              <h3
                className="text-2xl font-bold"
                style={{ color: 'var(--color-heading)' }}
              >
                San Fernando City Jail Recognition
              </h3>
              <p
                className="leading-relaxed"
                style={{ color: 'var(--color-card-text)' }}
              >
                Received official recognition from San Fernando City Jail for outstanding
                contribution to inmate rehabilitation through chess education. The program
                has been praised for promoting mental wellness and providing constructive
                activities for the incarcerated population.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <div
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    color: 'var(--color-muted)'
                  }}
                >
                  Prison Reform Advocate
                </div>
                <div
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    color: 'var(--color-muted)'
                  }}
                >
                  Youth Mentor
                </div>
              </div>
            </div>
            <div>
              <a
                href={getDataUrl('chess/recognitions/san_fernando_jail_inmates_recognition.pdf')}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div
                  className="p-8 rounded-xl border-2 border-dashed text-center transition-all duration-300 hover:shadow-card-hover cursor-pointer"
                  style={{
                    borderColor: 'var(--color-border-medium)',
                    backgroundColor: 'var(--color-theme-2)'
                  }}
                >
                  <Icon
                    name="download"
                    size="2xl"
                    className="mx-auto mb-4 opacity-50 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'var(--color-icon-default)' }}
                  />
                  <p
                    className="font-semibold mb-2"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    View Recognition Certificate
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    Click to open PDF
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="mt-12 text-center max-w-3xl mx-auto px-4">
          <blockquote className="text-lg sm:text-xl lg:text-2xl font-medium italic" style={{ color: 'var(--color-subheading)' }}>
            "Chess teaches us that every move matters, every decision has consequences,
            and there's always an opportunity for a better future."
          </blockquote>
          <p className="mt-4 text-xs sm:text-sm" style={{ color: 'var(--color-muted)' }}>
            - Srihaan Poddar, on teaching chess in correctional facilities
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrisonChessImpactSection;
